import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronDown, ChevronRight, AlertTriangle, Info, Pill, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Presentacion {
  id_presentacion: string;
  forma_farmaceutica: string;
  via_administracion: string;
  concentracion_valor: number;
  concentracion_unidad: string;
  empaque_tipo: string;
  unidades_por_empaque: number;
  es_pediatrico: boolean;
}

export interface Interaccion {
  principio_activo: string;
  severidad: 'CRITICA' | 'ALTA' | 'MODERADA' | 'LEVE';
}

export interface Posologia {
  adulto_diaria_max_mg: number | null;
  intervalo_min_horas: number | null;
  intervalo_rec_horas: number | null;
  peds_requiere_peso: boolean;
  peds_dosis_mg_kg: number | null;
  peds_diaria_max_mg_kg: number | null;
}

export interface VademecumDetalle {
  id: string;
  nombre_generico: string;
  grupo_fuente: string;
  nombres_comerciales: string[];
  requiere_receta: boolean;
  presentaciones: Presentacion[];
  posologia_general: Posologia | null;
  interacciones_clave: Interaccion[];
  tieneOverride?: boolean;
}

export interface MedicamentoSeleccionado {
  vademecumId: string;
  nombreGenerico: string;
  nombreComercial: string;
  grupo: string;
  formaFarmaceutica: string;
  concentracion: string;
  viaAdministracion: string;
  dosisSugerida: string;
  frecuenciaSugerida: string;
  posologia: Posologia | null;
  interacciones: Interaccion[];
  presentacionId: string;
}

interface Props {
  onSelect: (med: MedicamentoSeleccionado) => void;
  medicamentosActivos?: string[];
  placeholder?: string;
  className?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SEVERIDAD_COLOR: Record<string, string> = {
  CRITICA: 'text-red-700 bg-red-50 border-red-300',
  ALTA:    'text-orange-700 bg-orange-50 border-orange-300',
  MODERADA:'text-amber-700 bg-amber-50 border-amber-300',
  LEVE:    'text-blue-700 bg-blue-50 border-blue-300',
};

function formatConcentracion(p: Presentacion): string {
  return `${p.concentracion_valor} ${p.concentracion_unidad}`;
}

function formatFrecuencia(horas: number | null): string {
  if (!horas) return '';
  if (horas === 24) return 'una vez al día';
  if (horas === 12) return 'cada 12 horas';
  if (horas === 8)  return 'cada 8 horas';
  if (horas === 6)  return 'cada 6 horas';
  if (horas === 4)  return 'cada 4 horas';
  return `cada ${horas} horas`;
}

function InteraccionAlerta({ interacciones, medicamentosActivos }: {
  interacciones: Interaccion[];
  medicamentosActivos?: string[];
}) {
  if (!medicamentosActivos?.length || !interacciones.length) return null;
  const activos = medicamentosActivos.map(m => m.toLowerCase());
  const encontradas = interacciones.filter(i =>
    activos.some(a => a.includes(i.principio_activo.toLowerCase()) || i.principio_activo.toLowerCase().includes(a))
  );
  if (!encontradas.length) return null;

  return (
    <div className="mt-2 space-y-1">
      {encontradas.map((i, idx) => (
        <div key={idx} className={cn('flex items-center gap-2 px-2 py-1.5 rounded-lg border text-xs font-medium', SEVERIDAD_COLOR[i.severidad])}>
          <AlertTriangle size={12} className="shrink-0" />
          <span>Interacción con <strong>{i.principio_activo}</strong> — {i.severidad}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function VademecumPicker({ onSelect, medicamentosActivos, placeholder, className }: Props) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPres, setSelectedPres] = useState<string | null>(null);
  const [nombreComercial, setNombreComercial] = useState('');
  const [expandedGrupo, setExpandedGrupo] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: resultados = [] } = useQuery<Pick<VademecumDetalle, 'id' | 'nombre_generico' | 'grupo_fuente' | 'nombres_comerciales' | 'requiere_receta'>[]>({
    queryKey: ['vademecum-clinico-search', q],
    queryFn: () => api.get('/api/vademecum', { params: { q } }).then(r => r.data),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });

  const { data: detalle } = useQuery<VademecumDetalle>({
    queryKey: ['vademecum-clinico-detalle', selectedId],
    queryFn: () => api.get(`/api/vademecum-clinico/${selectedId}`).then(r => r.data),
    enabled: !!selectedId,
    staleTime: 300_000,
  });

  // Group results by grupo_fuente
  const byGrupo = resultados.reduce<Record<string, typeof resultados>>((acc, r) => {
    (acc[r.grupo_fuente] ??= []).push(r);
    return acc;
  }, {});

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSelectedId(null);
        setSelectedPres(null);
        setNombreComercial('');
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function handleSelectMed(id: string, nombreGen: string) {
    setSelectedId(id);
    setSelectedPres(null);
    setNombreComercial('');
    setQ(nombreGen);
  }

  function handleSelectPres(presId: string) {
    setSelectedPres(presId);
  }

  function handleConfirm() {
    if (!detalle || !selectedPres) return;
    const pres = detalle.presentaciones.find(p => p.id_presentacion === selectedPres);
    if (!pres) return;

    onSelect({
      vademecumId: detalle.id,
      nombreGenerico: detalle.nombre_generico,
      nombreComercial: nombreComercial || detalle.nombre_generico,
      grupo: detalle.grupo_fuente,
      formaFarmaceutica: pres.forma_farmaceutica,
      concentracion: formatConcentracion(pres),
      viaAdministracion: pres.via_administracion,
      dosisSugerida: formatConcentracion(pres),
      frecuenciaSugerida: formatFrecuencia(detalle.posologia_general?.intervalo_rec_horas ?? null),
      posologia: detalle.posologia_general,
      interacciones: detalle.interacciones_clave,
      presentacionId: pres.id_presentacion,
    });

    setOpen(false);
    setSelectedId(null);
    setSelectedPres(null);
    setQ('');
    setNombreComercial('');
  }

  const presSeleccionada = detalle?.presentaciones.find(p => p.id_presentacion === selectedPres);

  return (
    <div ref={ref} className={cn('relative', className)}>
      {/* Search input */}
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); setSelectedId(null); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder ?? 'Buscar medicamento (genérico o comercial)...'}
          className="w-full pl-8 pr-8 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        />
        {q && (
          <button type="button" onClick={() => { setQ(''); setSelectedId(null); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary">
            <X size={13} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-40 max-h-[520px] overflow-hidden flex flex-col">

          {/* Search results list */}
          {!selectedId && (
            <div className="overflow-y-auto flex-1">
              {q.trim().length < 2 && (
                <p className="px-3 py-3 text-xs text-text-secondary">Escribe al menos 2 caracteres para buscar</p>
              )}
              {q.trim().length >= 2 && resultados.length === 0 && (
                <p className="px-3 py-3 text-xs text-text-secondary">Sin resultados para "{q}"</p>
              )}
              {Object.entries(byGrupo).map(([grupo, items]) => (
                <div key={grupo}>
                  <button
                    type="button"
                    onClick={() => setExpandedGrupo(prev => prev === grupo ? null : grupo)}
                    className="flex items-center w-full px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-xs font-semibold text-text-secondary uppercase tracking-wide transition-colors"
                  >
                    {expandedGrupo === grupo ? <ChevronDown size={12} className="mr-1" /> : <ChevronRight size={12} className="mr-1" />}
                    {grupo} ({items.length})
                  </button>
                  {(expandedGrupo === grupo || Object.keys(byGrupo).length === 1) && items.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => handleSelectMed(r.id, r.nombre_generico)}
                      className="flex flex-col w-full text-left px-3 py-2 hover:bg-primary-50 transition-colors border-b border-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <Pill size={12} className="text-primary-400 shrink-0" />
                        <span className="text-sm font-medium text-text-primary">{r.nombre_generico}</span>
                        {r.requiere_receta && (
                          <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium">Receta</span>
                        )}
                      </div>
                      {r.nombres_comerciales.length > 0 && (
                        <span className="text-[11px] text-text-secondary ml-5 mt-0.5">
                          {r.nombres_comerciales.slice(0, 4).join(', ')}
                          {r.nombres_comerciales.length > 4 ? ` +${r.nombres_comerciales.length - 4}` : ''}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
              {/* Auto-expand when only one group */}
              {!expandedGrupo && Object.keys(byGrupo).length > 1 && q.trim().length >= 2 && resultados.length > 0 && (
                <p className="px-3 py-2 text-[11px] text-text-secondary italic">Haz clic en un grupo para expandir</p>
              )}
            </div>
          )}

          {/* Detail panel: presentaciones + posología */}
          {selectedId && detalle && (
            <div className="overflow-y-auto flex-1 p-3 space-y-3">
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => { setSelectedId(null); setSelectedPres(null); }}
                      className="text-xs text-primary-600 hover:underline">← Volver</button>
                  </div>
                  <p className="font-semibold text-text-primary mt-1">{detalle.nombre_generico}</p>
                  <p className="text-xs text-text-secondary">{detalle.grupo_fuente}</p>
                </div>
                {detalle.requiere_receta && (
                  <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Requiere receta</span>
                )}
              </div>

              {/* Nombre comercial */}
              {detalle.nombres_comerciales.length > 0 && (
                <div>
                  <label className="block text-[11px] font-medium text-text-secondary mb-1">Nombre comercial</label>
                  <select
                    value={nombreComercial}
                    onChange={e => setNombreComercial(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  >
                    <option value="">— Genérico —</option>
                    {detalle.nombres_comerciales.map(nc => (
                      <option key={nc} value={nc}>{nc}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Presentaciones */}
              <div>
                <label className="block text-[11px] font-medium text-text-secondary mb-1">Presentación *</label>
                <div className="space-y-1">
                  {detalle.presentaciones.map(p => (
                    <label key={p.id_presentacion} className={cn(
                      'flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm',
                      selectedPres === p.id_presentacion
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    )}>
                      <input
                        type="radio"
                        name="presentacion"
                        value={p.id_presentacion}
                        checked={selectedPres === p.id_presentacion}
                        onChange={() => handleSelectPres(p.id_presentacion)}
                        className="mt-0.5 accent-primary-500"
                      />
                      <div>
                        <span className="font-medium">{p.forma_farmaceutica.charAt(0) + p.forma_farmaceutica.slice(1).toLowerCase()}</span>
                        <span className="text-text-secondary"> {p.concentracion_valor} {p.concentracion_unidad}</span>
                        <span className="text-text-secondary"> · {p.via_administracion.toLowerCase()}</span>
                        {p.es_pediatrico && <span className="ml-1 text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full">Pediátrico</span>}
                        <div className="text-[11px] text-text-secondary mt-0.5">
                          {p.empaque_tipo} × {p.unidades_por_empaque} unidades
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Posología */}
              {detalle.posologia_general && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5">
                  <div className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 mb-1.5">
                    <Info size={11} /> Posología de referencia
                  </div>
                  <div className="text-[11px] text-blue-800 space-y-0.5">
                    {detalle.posologia_general.intervalo_rec_horas && (
                      <p>Frecuencia: cada {detalle.posologia_general.intervalo_rec_horas} h (mín. {detalle.posologia_general.intervalo_min_horas} h)</p>
                    )}
                    {detalle.posologia_general.adulto_diaria_max_mg && (
                      <p>Dosis adulto máx/día: {detalle.posologia_general.adulto_diaria_max_mg} mg</p>
                    )}
                    {detalle.posologia_general.peds_dosis_mg_kg && (
                      <p>Peds: {detalle.posologia_general.peds_dosis_mg_kg} mg/kg · máx {detalle.posologia_general.peds_diaria_max_mg_kg} mg/kg/día</p>
                    )}
                  </div>
                </div>
              )}

              {/* Interacciones con medicamentos activos */}
              {detalle.interacciones_clave.length > 0 && (
                <>
                  <InteraccionAlerta
                    interacciones={detalle.interacciones_clave}
                    medicamentosActivos={medicamentosActivos}
                  />
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
                      Ver todas las interacciones ({detalle.interacciones_clave.length})
                    </summary>
                    <div className="mt-1.5 space-y-1">
                      {detalle.interacciones_clave.map((i, idx) => (
                        <div key={idx} className={cn('flex items-center gap-2 px-2 py-1.5 rounded border text-[11px] font-medium', SEVERIDAD_COLOR[i.severidad])}>
                          <AlertTriangle size={10} className="shrink-0" />
                          {i.principio_activo} — {i.severidad}
                        </div>
                      ))}
                    </div>
                  </details>
                </>
              )}

              {/* Confirm button */}
              <button
                type="button"
                disabled={!selectedPres}
                onClick={handleConfirm}
                className={cn(
                  'w-full py-2 rounded-lg text-sm font-medium transition-colors',
                  selectedPres
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-gray-100 text-text-secondary cursor-not-allowed'
                )}
              >
                {selectedPres ? `Agregar ${presSeleccionada?.forma_farmaceutica.toLowerCase()} ${presSeleccionada?.concentracion_valor} ${presSeleccionada?.concentracion_unidad}` : 'Selecciona una presentación'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline (non-dropdown) variant for use in forms ────────────────────────────

export function VademecumPickerInline({ onSelect, medicamentosActivos }: {
  onSelect: (med: MedicamentoSeleccionado) => void;
  medicamentosActivos?: string[];
}) {
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPres, setSelectedPres] = useState<string | null>(null);
  const [nombreComercial, setNombreComercial] = useState('');
  const [expandedGrupo, setExpandedGrupo] = useState<string | null>(null);

  const { data: resultados = [] } = useQuery<Pick<VademecumDetalle, 'id' | 'nombre_generico' | 'grupo_fuente' | 'nombres_comerciales' | 'requiere_receta'>[]>({
    queryKey: ['vademecum-search', q],
    queryFn: () => api.get('/api/vademecum', { params: { q } }).then(r => r.data),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });

  const { data: detalle } = useQuery<VademecumDetalle>({
    queryKey: ['vademecum-clinico-detalle', selectedId],
    queryFn: () => api.get(`/api/vademecum-clinico/${selectedId}`).then(r => r.data),
    enabled: !!selectedId,
    staleTime: 300_000,
  });

  const byGrupo = resultados.reduce<Record<string, typeof resultados>>((acc, r) => {
    (acc[r.grupo_fuente] ??= []).push(r);
    return acc;
  }, {});

  function handleConfirm() {
    if (!detalle || !selectedPres) return;
    const pres = detalle.presentaciones.find(p => p.id_presentacion === selectedPres);
    if (!pres) return;

    onSelect({
      vademecumId: detalle.id,
      nombreGenerico: detalle.nombre_generico,
      nombreComercial: nombreComercial || detalle.nombre_generico,
      grupo: detalle.grupo_fuente,
      formaFarmaceutica: pres.forma_farmaceutica,
      concentracion: formatConcentracion(pres),
      viaAdministracion: pres.via_administracion,
      dosisSugerida: formatConcentracion(pres),
      frecuenciaSugerida: formatFrecuencia(detalle.posologia_general?.intervalo_rec_horas ?? null),
      posologia: detalle.posologia_general,
      interacciones: detalle.interacciones_clave,
      presentacionId: pres.id_presentacion,
    });

    setQ('');
    setSelectedId(null);
    setSelectedPres(null);
    setNombreComercial('');
  }

  const presSeleccionada = detalle?.presentaciones.find(p => p.id_presentacion === selectedPres);

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setSelectedId(null); setSelectedPres(null); }}
          placeholder="Buscar por nombre genérico o comercial..."
          className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        />
      </div>

      {/* Results */}
      {!selectedId && q.trim().length >= 2 && (
        <div className="border border-gray-100 rounded-xl overflow-hidden max-h-56 overflow-y-auto">
          {resultados.length === 0 && <p className="px-3 py-3 text-xs text-text-secondary">Sin resultados</p>}
          {Object.entries(byGrupo).map(([grupo, items]) => (
            <div key={grupo}>
              <button type="button"
                onClick={() => setExpandedGrupo(g => g === grupo ? null : grupo)}
                className="flex items-center w-full px-3 py-1.5 bg-gray-50 text-xs font-semibold text-text-secondary uppercase tracking-wide">
                {expandedGrupo === grupo ? <ChevronDown size={12} className="mr-1" /> : <ChevronRight size={12} className="mr-1" />}
                {grupo}
              </button>
              {(expandedGrupo === grupo || Object.keys(byGrupo).length === 1) && items.map(r => (
                <button key={r.id} type="button"
                  onClick={() => { setSelectedId(r.id); setQ(r.nombre_generico); }}
                  className="flex flex-col w-full text-left px-3 py-2 hover:bg-primary-50 border-b border-gray-50 transition-colors">
                  <span className="text-sm font-medium text-text-primary">{r.nombre_generico}</span>
                  <span className="text-[11px] text-text-secondary">{r.nombres_comerciales.slice(0, 3).join(', ')}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Detail */}
      {selectedId && detalle && (
        <div className="space-y-2 border border-gray-100 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { setSelectedId(null); setSelectedPres(null); }}
              className="text-xs text-primary-600 hover:underline">← Volver</button>
            <span className="text-sm font-semibold text-text-primary">{detalle.nombre_generico}</span>
          </div>

          {detalle.nombres_comerciales.length > 0 && (
            <select value={nombreComercial} onChange={e => setNombreComercial(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
              <option value="">— Genérico —</option>
              {detalle.nombres_comerciales.map(nc => <option key={nc} value={nc}>{nc}</option>)}
            </select>
          )}

          <div className="space-y-1">
            <p className="text-[11px] font-medium text-text-secondary">Presentación *</p>
            {detalle.presentaciones.map(p => (
              <label key={p.id_presentacion} className={cn(
                'flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm transition-colors',
                selectedPres === p.id_presentacion ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
              )}>
                <input type="radio" name="pres-inline" value={p.id_presentacion}
                  checked={selectedPres === p.id_presentacion}
                  onChange={() => setSelectedPres(p.id_presentacion)}
                  className="accent-primary-500" />
                <span>{p.forma_farmaceutica.charAt(0) + p.forma_farmaceutica.slice(1).toLowerCase()} {p.concentracion_valor} {p.concentracion_unidad}</span>
                <span className="text-text-secondary text-[11px]">· {p.via_administracion.toLowerCase()}</span>
                {p.es_pediatrico && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1 py-0.5 rounded-full">Pediátrico</span>}
              </label>
            ))}
          </div>

          <InteraccionAlerta interacciones={detalle.interacciones_clave} medicamentosActivos={medicamentosActivos} />

          {detalle.posologia_general?.intervalo_rec_horas && (
            <p className="text-[11px] text-blue-700 bg-blue-50 px-2 py-1.5 rounded border border-blue-100">
              Frecuencia sugerida: cada {detalle.posologia_general.intervalo_rec_horas} horas
            </p>
          )}

          <button type="button" disabled={!selectedPres} onClick={handleConfirm}
            className={cn('w-full py-2 rounded-lg text-sm font-medium transition-colors',
              selectedPres ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-gray-100 text-text-secondary cursor-not-allowed')}>
            {selectedPres
              ? `Agregar ${presSeleccionada?.forma_farmaceutica.toLowerCase()} ${presSeleccionada?.concentracion_valor} ${presSeleccionada?.concentracion_unidad}`
              : 'Selecciona una presentación'}
          </button>
        </div>
      )}
    </div>
  );
}
